/**
 * Converts a given amount in cents to USD.
 * The input cents is rounded because we expect it to be an integer.
 *
 * @param cents - The amount in cents to convert.
 * @returns The converted amount in USD.
 */
export function convertCentsToUsd(cents: number | string): number {
  return Math.round(Number(cents)) / 100
}

/**
 * Converts a given amount in USD to cents.
 *
 * @param amount - The amount in USD to convert.
 * @returns The converted amount in cents.
 */
export function convertUsdToCents(amount: number | string): number {
  return Math.round(Number(amount) * 100)
}

/**
 * Formats a given value as a currency string.
 *
 * @param value - The value to format. Can be a number or a string that can be parsed to a number.
 * @param options - An optional object containing:
 *   - currencyCode: The ISO 4217 currency code (default is "usd").
 *   - locale: The locale to use for formatting (default is "en-US").
 *   - decimals: The number of decimal places to use (default is 2).
 * @returns The formatted currency string.
 * @throws  If the input value cannot be parsed to a number.
 */
export function formatAsCurrency(
  value: string | number,
  options?: {
    currencyCode?: string
    locale?: string
    decimals?: number
  },
): string {
  const { currencyCode = "usd", locale = "en-US", decimals = 2 } = options || {}

  const numericValue = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(numericValue)) {
    throw new Error("Input must be a parsable number or numeric string")
  }
  return numericValue.toLocaleString(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}
